import { shuffle } from '../engine/rng'
import {
  DAILY_MISSION_COUNT,
  MISSION_POOL,
  MISSION_TIER_QUOTA,
  type MissionCategory,
  type MissionDef,
  type MissionId,
  type MissionTier,
} from './catalog'
import type { MissionMetric } from './metrics'

/**
 * FNV-1a 32-bit hash so the same account + UTC day always rolls the same board.
 */
export function hashSeed(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) || 1
}

/** UTC calendar day `YYYY-MM-DD`. */
export function dayKeyUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

function candidates(
  category: MissionCategory,
  used: ReadonlySet<MissionMetric>,
  tier?: MissionTier,
): MissionDef[] {
  return MISSION_POOL.filter((row) => {
    if (row.category !== category) return false
    if (tier !== undefined && row.tier !== tier) return false
    return !used.has(row.metric)
  })
}

function draw(
  pool: readonly MissionDef[],
  state: number,
): { item: MissionDef | null; rngState: number } {
  if (pool.length === 0) return { item: null, rngState: state }
  const shuffled = shuffle([...pool], state)
  return { item: shuffled.items[0] ?? null, rngState: shuffled.rngState }
}

/**
 * Today's 10 missions: one metric each, quota of 3 easy / 4 medium / 3 hard,
 * ordered easy to hard.
 */
export function rollDailyMissions(userId: string, dayKey: string): MissionId[] {
  let state = hashSeed(`${userId}:${dayKey}`)
  const picked: MissionDef[] = []
  const used = new Set<MissionMetric>()

  for (const category of Object.keys(MISSION_TIER_QUOTA) as MissionCategory[]) {
    for (const tier of MISSION_TIER_QUOTA[category]) {
      let drawn = draw(candidates(category, used, tier), state)
      if (!drawn.item) drawn = draw(candidates(category, used), state)
      state = drawn.rngState
      if (!drawn.item) continue
      picked.push(drawn.item)
      used.add(drawn.item.metric)
    }
  }

  picked.sort((a, b) => a.tier - b.tier)
  return picked.slice(0, DAILY_MISSION_COUNT).map((row) => row.id)
}

/** Milliseconds until the next UTC midnight. */
export function msUntilUtcMidnight(now: Date = new Date()): number {
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  )
  return Math.max(0, next - now.getTime())
}
